import { create } from "zustand";
import { useProjectStore } from "../core_stores/useProjectStore";
import { isRollbackError } from "../types/chatTypeGuards";
import {
  ProjectChatAssistantMessage,
  ProjectChatElement,
} from "./chat_messages/chatModels";
import { useChatMessagesStore } from "./chat_messages/useChatMessagesStore";

interface RollbackErrorHandlerActions {
  initialize: () => void;
  abort: () => Promise<void>;
  retry: () => Promise<void>;
  dispose: () => void;
}

interface RollbackErrorHandlerState {
  isRollbackError: boolean;
  isProcessing: boolean;

  actions: RollbackErrorHandlerActions;
}

const useRollbackErrorHandlerStore = create<RollbackErrorHandlerState>(
  (set, get) => {
    let unsubscribeChatElements: (() => void) | null = null;
    let unsubscribeProjectLatestMsg: (() => void) | null = null;

    function hasRollbackError(chatElements: ProjectChatElement[]): boolean {
      if (chatElements.length === 0) {
        return false;
      }
      const lastMessage = chatElements[chatElements.length - 1];
      return (
        lastMessage instanceof ProjectChatAssistantMessage &&
        lastMessage.type === "error" &&
        isRollbackError(lastMessage.value)
      );
    }

    return {
      isRollbackError: false,
      isProcessing: false,

      actions: {
        initialize: () => {
          unsubscribeChatElements = useChatMessagesStore.subscribe((state) => {
            set({
              isRollbackError: hasRollbackError(
                state.chatElements.elements ?? [],
              ),
            });
          });
          unsubscribeProjectLatestMsg = useProjectStore.subscribe(
            (state) => state.latestMsg,
            () => {
              if (get().isProcessing) {
                set({ isProcessing: false });
              }
            },
          );
        },

        abort: async () => {
          set({ isProcessing: true });
          const elements =
            useChatMessagesStore.getState().chatElements.elements;
          const rollbackError = elements[
            elements.length - 1
          ] as ProjectChatAssistantMessage;

          if (!isRollbackError(rollbackError.value)) {
            throw new Error("Last message is not a rollback error");
          }

          const { targetCheckpointId, originalCheckpointId } =
            rollbackError.value.rollbackDetails;

          const projectActions = useProjectStore.getState().actions;
          await projectActions.rollback(
            originalCheckpointId,
            targetCheckpointId,
          );
        },

        retry: async () => {
          set({ isProcessing: true });
          const elements =
            useChatMessagesStore.getState().chatElements.elements;
          const rollbackError = elements[
            elements.length - 1
          ] as ProjectChatAssistantMessage;

          if (!isRollbackError(rollbackError.value)) {
            throw new Error("Last message is not a rollback error");
          }

          const { targetCheckpointId, originalCheckpointId } =
            rollbackError.value.rollbackDetails;

          const projectActions = useProjectStore.getState().actions;
          await projectActions.rollback(
            targetCheckpointId,
            originalCheckpointId,
          );
        },

        dispose: () => {
          if (unsubscribeChatElements) {
            unsubscribeChatElements();
            unsubscribeChatElements = null;
          }
          if (unsubscribeProjectLatestMsg) {
            unsubscribeProjectLatestMsg();
            unsubscribeProjectLatestMsg = null;
          }
          set({ isRollbackError: false, isProcessing: false });
        },
      },
    };
  },
);

export const useIsRollbackError = () =>
  useRollbackErrorHandlerStore((state) => state.isRollbackError);

export const useIsProcessingRollbackError = () =>
  useRollbackErrorHandlerStore((state) => state.isProcessing);

export const useRollbackErrorHandlerActions = () =>
  useRollbackErrorHandlerStore((state) => state.actions);
